import { useConstant } from '@wearejh/react-hooks/lib/useConstant';
import { useDispatch } from 'react-redux';
import { BehaviorSubject, concat, merge, Observable, of, Subject } from 'rxjs';
import { useCallback, useEffect, useRef } from 'react';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import zenscroll from 'zenscroll';

import { useDeps } from 'src/hooks/useDeps';
import {
    addSelection,
    AggregationSelections,
    MappedAggregations,
    removeSelection,
    selectionsFromSearch,
    serializeSelectionsToUrl,
} from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { useHistoryLocationObservable } from 'src/hooks/router-hooks';
import { createDebug } from 'src/util/debug';

const debug = createDebug('useLayeredNav');

export function useLayeredNav(mappedAggregations: MappedAggregations, scrollSelector = '[data-smooth-scroll]') {
    const location = useHistoryLocationObservable();
    const selected = selectionsFromSearch(mappedAggregations, location.value.search);
    const dispatch = useDispatch();
    const selection$ = useConstant(() => new BehaviorSubject<AggregationSelections>(selected));
    const change$ = useConstant(() => new Subject<{ checked: boolean; code: string; label: string }>());
    const removeAll$ = useConstant(() => new Subject<any>());
    const { replace } = useDeps();

    useEffect(() => {
        function createNextUrl(prevSelections: AggregationSelections): string {
            const { pathname, search } = window.location;
            const params = serializeSelectionsToUrl(mappedAggregations, prevSelections, search);
            const nextUrl = `${pathname}?${params}`;
            return nextUrl;
        }

        /**
         * Handle a 'remove all' event.
         * This will look at all options and deselect them in the dom,
         * without triggering any events (as we don't want to cause multiple urls updates)
         */
        const removes$ = removeAll$.pipe(
            withLatestFrom(selection$),
            tap(([, prevSelections]) => {
                Object.keys(prevSelections).forEach((key) => {
                    const match = document.querySelectorAll(`input[id^="${key}"]`) as NodeListOf<HTMLInputElement>;
                    match.forEach((elem: HTMLInputElement) => {
                        elem.checked = false;
                    });
                });
            }),
            map(() => ({})),
        );
        /**
         * The update event is when a checkbox goes from 'checked'
         * to 'unchecked' (or the opposite)
         */
        const updates$ = change$.pipe(
            withLatestFrom(selection$),
            map(([change, prevState]) => {
                if (change.checked) {
                    return addSelection(prevState, change.code, change.label);
                }
                return removeSelection(prevState, change.code, change.label);
            }),
        );
        /**
         * Now listen to remove + update events and update the URL to match
         */
        const sub = merge(updates$, removes$)
            .pipe(
                tap((selections) => {
                    debug('next selections = %o', selections);
                    selection$.next(selections);
                }),
                switchMap((selections) => {
                    return concat(scrollModalToTop(scrollSelector), of(selections));
                }),
                tap((selections) => dispatch(replace(createNextUrl(selections)))),
            )
            .subscribe();

        return () => {
            sub.unsubscribe();
        };
    }, [change$, dispatch, mappedAggregations, removeAll$, replace, scrollSelector, selection$]);

    const onChange = useCallback(
        (checked, code, label) => {
            change$.next({ checked, code, label });
        },
        [change$],
    );
    /**
     * When removing a single item, we modify the DOM and then fire
     * an event so that react sees the change
     */
    const ref = useRef<HTMLDivElement | null>(null);
    const onRemove = useCallback(
        (code, value, label) => {
            const id = `${code}-${value}`;
            const match = document.getElementById(id) as HTMLInputElement | null;
            if (match !== null) {
                match.checked = false;
                match.dispatchEvent(new CustomEvent('change', { bubbles: true }));
            } else {
                change$.next({ checked: false, code, label });
            }
        },
        [change$],
    );
    /**
     * A proxy for removing all selections
     */
    const onRemoveAll = useCallback(() => {
        removeAll$.next();
    }, [removeAll$]);
    return { ref, onRemove, onRemoveAll, onChange, selected, selection$ };
}

function scrollModalToTop(selector: string): Observable<never> {
    return new Observable((obs) => {
        const defaultDuration = 500;
        const element = document.querySelector(selector);
        const myScroller = zenscroll.createScroller(element, defaultDuration, 30);
        const y = myScroller.getY();
        if (y < 50) {
            return obs.complete();
        }
        myScroller.toY(0, 500, () => obs.complete());
    });
}
